import { useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import { SEARCH_DEBOUNCE_TIME } from "@constants";
import { useDebouncedEffect } from "@hooks/useDebouncedEffect";
import { Button } from "@design-system/Button/Button";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerStatus } from "@design-system/Picker/Picker.types";
import { PlusIcon } from "@icons";
import {
    buildCompanyItems,
    COMPANY_PAGE_SIZE,
    fetchCompanyPage,
} from "./PickerPlaygroundCompanies";

const companyItems = buildCompanyItems({
    hasDescriptions: false,
    hasRightText: false,
});

/** Stands in for a real paged, searchable endpoint. */
const fetchCompanies = ({
    search,
    offset,
}: {
    search: string;
    offset: number;
}) => fetchCompanyPage({ items: companyItems, search, offset });

// #region paged
const useCompanyPages = () => {
    const [typedSearch, setTypedSearch] = useState("");
    const [search, setSearch] = useState("");
    useDebouncedEffect(
        () => setSearch(typedSearch),
        [typedSearch],
        SEARCH_DEBOUNCE_TIME,
    );
    const query = useInfiniteQuery({
        queryKey: ["pickerPlaygroundCompanies", search],
        queryFn: ({ pageParam }) =>
            fetchCompanies({ search, offset: pageParam }),
        initialPageParam: 0,
        getNextPageParam: (lastPage, pages) =>
            lastPage.length === COMPANY_PAGE_SIZE
                ? pages.length * COMPANY_PAGE_SIZE
                : undefined,
    });

    let loadMoreStatus: PickerStatus = "idle";
    if (query.isFetchingNextPage) {
        loadMoreStatus = "loading";
    } else if (query.isFetchNextPageError) {
        loadMoreStatus = "error";
    }
    const status: PickerStatus = query.isPending ? "loading" : "idle";

    return {
        items: query.data?.pages.flat() ?? [],
        status,
        onInputValueChange: setTypedSearch,
        loadMore: {
            hasMore: query.hasNextPage,
            onLoadMore: () => void query.fetchNextPage(),
            loadMoreStatus,
        },
    };
};

export const PagedExample = () => {
    const [value, setValue] = useState<string | null>(null);
    const companies = useCompanyPages();
    return (
        <Picker.Select
            items={companies.items}
            status={companies.status}
            {...companies.loadMore}
            searchable
            filterable={false}
            onInputValueChange={companies.onInputValueChange}
            searchPlaceholder="Search companies"
            loadingLabel="Loading companies"
            value={value}
            onValueChange={setValue}
            placeholder="Choose a company"
        />
    );
};
// #endregion

// #region pagedComposed
export const PagedComposedExample = () => {
    const companies = useCompanyPages();
    return (
        <Picker.Select
            searchable
            {...companies.loadMore}
            onInputValueChange={companies.onInputValueChange}
        >
            <Picker.Trigger
                render={<Button variant="secondary">Choose company</Button>}
            />
            <Picker.Popup>
                <Picker.Search placeholder="Search companies" />
                <Picker.List>
                    {companies.items.map((item) => (
                        <Picker.Item key={item.value} value={item.value}>
                            {item.label}
                        </Picker.Item>
                    ))}
                    <Picker.LoadMore />
                    <Picker.Footer>
                        <Picker.Item
                            leading={<PlusIcon />}
                            onSelect={() => undefined}
                        >
                            Add company
                        </Picker.Item>
                    </Picker.Footer>
                </Picker.List>
            </Picker.Popup>
        </Picker.Select>
    );
};
// #endregion
